/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {

  /*
   * Bug fix (Core Web Vitals / INP): dispatchResize used to be implicit -
   * this constructor always fired 'resize.resize-select' on window, 10ms
   * after every single call. global.js's SelectWidth class listens for
   * that exact event and, on every firing, loops over EVERY ".resize-select"
   * element on the page (sort-by, currency, language selects, etc.),
   * inserting a temporary <select> next to each one and immediately
   * reading its clientWidth - a forced synchronous layout ("layout
   * thrashing") per element, repeated for every .resize-select on the
   * page, every time this constructor runs.
   *
   * That's fine for the one real page-load call at the bottom of this
   * file (selects genuinely need their initial width measured once).
   * But renderSection() below also calls `new FacetsToggle()` after every
   * single AJAX facet response - i.e. after every filter click - and none
   * of those selects' selected options change just because the product
   * grid got re-filtered, so re-running that whole global sweep every
   * time was pure wasted main-thread work sitting in the same task as
   * the interaction.
   *
   * dispatchResize defaults to true (page load, and any other caller,
   * behave exactly as before). renderSection() passes false unless the
   * AJAX response itself actually contains a .resize-select element, so
   * a sort/currency/language select that's genuinely new or changed in
   * that response still gets measured correctly - only the redundant,
   * nothing-actually-changed case is skipped.
   */
  constructor(dispatchResize = true) {
    this.container = document.getElementById('Facet-Drawer');
    let button = document.getElementById('Facets-Toggle');

    // Add functionality to buttons
    if (button && !button.dataset.facetsToggleBound) {
      button.dataset.facetsToggleBound = 'true';
      button.addEventListener('click', (e) => {
        e.preventDefault();
        document.getElementsByTagName('body')[0].classList.toggle('open-cc');
        this.container.classList.toggle('active');
      });
    }
    if (dispatchResize) {
      setTimeout(function () {
        window.dispatchEvent(new Event('resize.resize-select'));
      }, 10);
    }
  }
}


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    /*
     * Debounce is only useful for coalescing a *stream* of 'input' events
     * into one request — e.g. someone typing into a free-text price field.
     * A checkbox or radio click is a single, discrete event with nothing to
     * coalesce, so debouncing it just adds pure waiting time on top of the
     * network request with zero benefit. Those now submit immediately;
     * text/number inputs (the min/max price fields) still go through the
     * debounce so typing a price doesn't fire a request per keystroke.
     */
    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 300);

    this.querySelector('form').addEventListener('input', (event) => {
      if (event.target && (event.target.type === 'checkbox' || event.target.type === 'radio')) {
        this.onSubmitHandler(event);
      } else {
        this.debouncedOnSubmit(event);
      }
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (FacetFiltersForm.requestController) FacetFiltersForm.requestController.abort();
    FacetFiltersForm.requestController = new AbortController();
    const requestId = ++FacetFiltersForm.requestId;
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.getElementsByClassName('thb-filter-count');
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

    for (var item of container) {
      item.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event, requestId, FacetFiltersForm.requestController.signal);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

  }

  static parseHTML(html) {
    return typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
  }

  static renderSection(html, event) {
    const parsed = FacetFiltersForm.parseHTML(html);
    FacetFiltersForm.renderFilters(parsed, event);
    FacetFiltersForm.renderProductGridContainer(parsed, event);
    FacetFiltersForm.renderProductCount(parsed);
    // See the dispatchResize comment on FacetsToggle above: only re-run
    // the global .resize-select sweep when this AJAX response actually
    // contains one of those selects.
    const needsResize = !!parsed.querySelector('.resize-select');
    new FacetsToggle(needsResize);
  }

  static renderSectionFromFetch(url, event, requestId, signal) {
    return fetch(url, { signal })
      .then(response => {
        if (!response.ok) throw new Error('Filter request failed');
        return response.text();
      })
      .then(html => {
        if (requestId !== FacetFiltersForm.requestId) return;
        FacetFiltersForm.filterData.push({ html, url });
        if (FacetFiltersForm.filterData.length > 20) FacetFiltersForm.filterData.shift();
        FacetFiltersForm.renderSection(html, event);
      })
      .catch(error => {
        if (error.name === 'AbortError' || requestId !== FacetFiltersForm.requestId) return;
        // Fall back to normal navigation if AJAX fails, preserving the filters.
        window.location.assign(window.location.href);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    FacetFiltersForm.renderSection(FacetFiltersForm.filterData.find(filterDataUrl).html, event);
  }

  static renderProductGridContainer(html, event) {
    const container = document.getElementById('ProductGridContainer');
    const incoming = FacetFiltersForm.parseHTML(html)
      .getElementById('ProductGridContainer');
    if (!container || !incoming) return;

    // Read the current UI when the response arrives, so a tab changed
    // while the request was pending is preserved too.
    const currentBar = container.querySelector('.shopby');
    const nextBar = incoming.querySelector('.shopby');
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const fromShopBy = !!(event && event.target &&
      event.target.closest && event.target.closest('.shopby'));

    if (currentBar && nextBar) {
      const activeButton = currentBar.querySelector('.shopby__tab.is-active');
      let tab = activeButton ? activeButton.getAttribute('data-shopby-tab') : null;
      const buttons = Array.from(nextBar.querySelectorAll('.shopby__tab'));
      if (!buttons.some(button => button.getAttribute('data-shopby-tab') === tab)) {
        tab = mobile ? null : (buttons[0] && buttons[0].getAttribute('data-shopby-tab'));
      }
      if (mobile && !currentBar.classList.contains('shopby--open')) tab = null;

      // Prepare the detached HTML. Size never enters the live page as
      // the active panel when the shopper has Colour or Product Type open.
      nextBar.classList.toggle('shopby--open', mobile && !!tab);
      buttons.forEach(button => {
        const active = button.getAttribute('data-shopby-tab') === tab;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
        button.setAttribute('aria-expanded', String(active));
      });
      nextBar.querySelectorAll('[data-shopby-panel]').forEach(panel => {
        panel.hidden = panel.getAttribute('data-shopby-panel') !== tab;
      });
    }

    container.innerHTML = incoming.innerHTML;

    // Keep the shopper at the controls they are using. Other filtering
    // and pagination retain the theme's existing scroll behavior.
    if (fromShopBy) return;

    /*
     * Bug fix (Core Web Vitals / INP): this used to read
     * getBoundingClientRect() and getComputedStyle() right here, in the
     * very same task as the container.innerHTML swap two lines up. Both of
     * those reads force the browser to synchronously flush layout for
     * whatever DOM work is pending before they can return a value - and
     * the innerHTML swap just above is, on this page, up to ~49 product
     * cards' worth of brand-new markup (every image, every card). Reading
     * geometry immediately after writing that much DOM is the classic
     * "layout thrashing" pattern: it forces all of that layout
     * recalculation to happen synchronously inside this one task, instead
     * of letting the browser lay out and paint the new grid on its own
     * schedule. That is the single biggest scriptable culprit behind the
     * long, blocking task measured after a filter click on the live
     * collection page (INP 217ms field data). Moving the read + scroll
     * into a requestAnimationFrame callback pushes it past the browser's
     * own layout/paint step for this task, so the forced synchronous
     * layout no longer sits inside the interaction's processing time. The
     * scroll behavior itself (target position, the >4px threshold,
     * instant vs smooth) is unchanged.
     */
    requestAnimationFrame(() => {
      if (!document.body.contains(container)) return;
      let top = container.getBoundingClientRect().top + document.documentElement.scrollTop - parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) - 30;

      if (Math.abs(document.documentElement.scrollTop - top) > 4) {
        window.scrollTo({
          top: top,
          left: 0,
          behavior: "auto"
        });
      }
    });
  }

  static renderProductCount(html) {
    const countHtml = FacetFiltersForm.parseHTML(html).getElementById('ProductCount');
    const container = document.getElementsByClassName('thb-filter-count');

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove('loading');
      }
    }

  }

  static renderFilters(html, event) {
    const parsedHTML = FacetFiltersForm.parseHTML(html);

    const facetDetailsElements =
      parsedHTML.querySelectorAll('#FacetFiltersForm collapsible-row, #FacetFiltersFormMobile collapsible-row');
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('collapsible-row') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(element => !matchesIndex(element));
    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    facetsToRender.forEach((element) => {
      document.querySelector(`collapsible-row[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    if (countsToRender) FacetFiltersForm.renderCounts(countsToRender, event.target.closest('collapsible-row'));
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-filter-count'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');

    if (sourceElement && targetElement) {
      target.querySelector('.facets__selected').outerHTML = source.querySelector('.facets__selected').outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({
      searchParams
    }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      }];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    // Server-rendered is-active classes must track the live checked state,
    // including the radio that the browser just unchecked automatically.
    form.querySelectorAll('.shopby-input').forEach(input => {
      const label = input.closest('.shopby-pill, .shopby-swatch');
      if (label) label.classList.toggle('is-active', input.checked);
    });
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    if (searchParams.get('filter.v.price.gte') === "0.00") {
      searchParams.delete('filter.v.price.gte');
    }
    if (document.querySelector('.price_slider')) {
      if (searchParams.get('filter.v.price.lte') === parseFloat(document.querySelector('.price_slider').dataset.max).toFixed(2)) {
        searchParams.delete('filter.v.price.lte');
      }
    }
    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.requestId = 0;
FacetFiltersForm.requestController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('a').forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define('facet-remove', FacetRemove);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {

  constructor() {
    super();
  }
  connectedCallback() {
    let slider = this.querySelector('.price_slider'),
      amounts = this.querySelector('.price_slider_amount'),
      args = {
        start: [parseFloat(slider.dataset.minValue || 0), parseFloat(slider.dataset.maxValue || slider.dataset.max)],
        connect: true,
        step: 10,
        direction: document.dir,
        handleAttributes: [
          { 'aria-label': 'lower' },
          { 'aria-label': 'upper' },
        ],
        range: {
          'min': 0,
          'max': parseFloat(slider.dataset.max)
        }
      },
      event = new CustomEvent('input'),
      form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');

    if (slider.classList.contains('noUi-target')) {
      slider.noUiSlider.destroy();
    }
    noUiSlider.create(slider, args);

    slider.noUiSlider.on('update', function (values) {
      amounts.querySelector('.field__input_min').value = values[0];
      amounts.querySelector('.field__input_max').value = values[1];
    });
    slider.noUiSlider.on('change', function (values) {
      form.querySelector('form').dispatchEvent(event);
    });
  }
}
customElements.define('price-slider', PriceSlider);

window.addEventListener('load', () => {
  new FacetsToggle();
});